import { weekOf, weekBounds } from "@/lib/semanas";

export const CAPTURA = [
  ["gasto_corporativo", "Gasto Corporativo"], ["provisiones_corporativas", "Provisiones Corporativas"], ["otros_programas", "Otros Programas"],
  ["apoyos_corporativos", "Apoyos Corporativos"], ["inza", "INZA"], ["impuestos_federales", "IMPUESTOS FEDERALES"],
  ["costo_bancario", "COSTO BANCARIO"], ["deuda_bancaria", "DEUDA BANCARIA"], ["inversiones", "INVERSIONES"],
];
const CORP = ["gasto_corporativo", "provisiones_corporativas", "otros_programas", "apoyos_corporativos", "inza"];
const L = Object.fromEntries(CAPTURA);
// Renglones en el orden del Excel (sep = renglón de separación).
export const FILAS = [
  ...CORP.map((k) => ({ k, label: L[k] })),
  { k: "corporativos", label: "CORPORATIVOS", bold: true }, { sep: 1 },
  { k: "impuestos_federales", label: L.impuestos_federales, bold: true }, { sep: 2 },
  { k: "costo_bancario", label: L.costo_bancario, bold: true }, { sep: 3 },
  { k: "deuda_bancaria", label: L.deuda_bancaria, bold: true }, { sep: 4 },
  { k: "inversiones", label: L.inversiones, bold: true }, { sep: 5 },
  { k: "total", label: "TOTAL BLOQUE 3", bold: true, total: true },
];
const KEYS = FILAS.filter((f) => f.k).map((f) => f.k);

const mensualGF = (m) => {
  m.corporativos = CORP.reduce((s, k) => s + m[k], 0);
  m.total = m.corporativos + m.impuestos_federales + m.costo_bancario + m.deuda_bancaria + m.inversiones;
  return m;
};

export const GF_DEF = {
  mod: "gasto-fijo", titulo: "Gasto Fijo", bloque: "Bloque 3", totalLabel: "Total Bloque 3", cap: "gasto_fijo_cap",
  descripcion: "Ejercicios mensuales del Bloque 3 con aplicación diaria y semanal.",
  captura: CAPTURA, filas: FILAS, totales: mensualGF, inicioMes: true, semanal: true,
  grupos: [["Corporativos", CAPTURA.slice(0, 5)], ["Otros conceptos del Bloque 3", CAPTURA.slice(5)]],
};

const GP_CAPTURA = [
  ["nomina_planta", "Presupuesto Nómina Planta"], ["gastos_planta", "Presupuesto Gastos Planta"], ["imss_sua_planta", "Presupuesto IMSS/SUA Planta"],
  ["extra_discrecionales", "Extraordinarios Discrecionales"], ["extra_no_discrecionales", "Extraordinarios No Discrecionales"],
  ["apoyos_planta_inversion", "Apoyos Planta Inversión"], ["provisiones_planta", "PROVISIONES PLANTA"],
];
const GPL = Object.fromEntries(GP_CAPTURA);
// Igual que el Excel v2: PRESUPUESTO PLANTA y EXTRAORDINARIOS son sumas; PROVISIONES PLANTA se captura y no entra al SUBTOTAL.
export const GP_DEF = {
  mod: "gestion-planta", titulo: "Gestión Planta", bloque: "Bloque 2", totalLabel: "Subtotal Bloque 2", cap: "gestion_planta_cap",
  descripcion: "Ejercicios mensuales del Bloque 2 con aplicación diaria y semanal, y Variables de Gestión del mes.",
  captura: GP_CAPTURA, variables: true, inicioMes: true, historialMes: true, semanal: true,
  filas: [
    { titulo: "GASTOS:" },
    ...["nomina_planta", "gastos_planta", "imss_sua_planta"].map((k) => ({ k, label: GPL[k] })),
    { k: "presupuesto_planta", label: "PRESUPUESTO PLANTA", bold: true }, { sep: 1 },
    ...["extra_discrecionales", "extra_no_discrecionales", "apoyos_planta_inversion"].map((k) => ({ k, label: GPL[k] })),
    { k: "extraordinarios", label: "EXTRAORDINARIOS", bold: true }, { sep: 2 },
    { k: "provisiones_planta", label: "PROVISIONES PLANTA", bold: true }, { sep: 3 },
    { k: "total", label: "SUBTOTAL BLOQUE 2", bold: true, total: true },
  ],
  totales: (m) => {
    m.presupuesto_planta = m.nomina_planta + m.gastos_planta + m.imss_sua_planta;
    m.extraordinarios = m.extra_discrecionales + m.extra_no_discrecionales + m.apoyos_planta_inversion;
    m.total = m.presupuesto_planta + m.extraordinarios;
    return m;
  },
  grupos: [["Gastos de planta", GP_CAPTURA.slice(0, 3)], ["Extraordinarios", GP_CAPTURA.slice(3, 6)], ["Provisiones", GP_CAPTURA.slice(6)]],
};

const keysOf = (def) => def.filas.filter((f) => f.k).map((f) => f.k);
const mensual = (imp, def) => def.totales(Object.fromEntries(def.captura.map(([k]) => [k, Number(imp?.[k]) || 0])));

// Primer ejercicio: G = F / días. Siguientes: K = ((J − F) / días) + G. Semanal = diaria × 7.
// Semanas (domingo a sábado, numeración de CNE) con al menos un día dentro del mes.
export function semanasDelMes(year, month) {
  const out = [];
  const last = new Date(year, month, 0).getDate();
  for (let d = 1; d <= last; d += 1) {
    const w = weekOf(new Date(year, month - 1, d));
    if (!out.some((x) => x.year === w.year && x.week === w.week)) out.push(w);
  }
  return out;
}

// Días de aplicación: desde el primer día de la semana dentro del mes hasta fin de mes (igual que el backend).
export function aplicacion(year, month, wy, wk) {
  const [s] = weekBounds(wy, wk);
  const ini = Math.max(s.getTime(), Date.UTC(year, month - 1, 1));
  const fin = Date.UTC(year, month, 0);
  return Math.round((fin - ini) / 86400000) + 1;
}

// Días de la semana que caen dentro del mes (referencia en el selector).
export function diasSemanaEnMes(year, month, wy, wk) {
  const [s, e] = weekBounds(wy, wk);
  const ini = Math.max(s.getTime(), Date.UTC(year, month - 1, 1));
  const fin = Math.min(e.getTime(), Date.UTC(year, month, 0));
  return Math.round((fin - ini) / 86400000) + 1;
}

export function calcEjercicios(ejs, def = GF_DEF) {
  const KEYS = keysOf(def);
  const out = [];
  ejs.forEach((e, i) => {
    const m = mensual(e.importes, def);
    const prev = out[i - 1];
    const diaria = Object.fromEntries(KEYS.map((k) => [k, prev ? (m[k] - prev.mensual[k]) / e.dias + prev.diaria[k] : m[k] / e.dias]));
    const semanal = Object.fromEntries(KEYS.map((k) => [k, diaria[k] * 7]));
    out.push({ ...e, mensual: m, diaria, semanal });
  });
  return out;
}

// Comprobación: diaria de cada tramo × días del tramo; acumulado y diferencia contra el último presupuesto.
export function comprobacion(calc, def = GF_DEF) {
  const KEYS = keysOf(def);
  const tramos = calc.map((e, i) => ({ e, dias: e.dias - (calc[i + 1]?.dias ?? 0), siguiente: calc[i + 1]?.inicio }));
  const last = calc[calc.length - 1];
  const filas = Object.fromEntries(KEYS.map((k) => {
    const vals = tramos.map((t) => t.e.diaria[k] * t.dias);
    const acum = vals.reduce((a, b) => a + b, 0);
    return [k, { vals, acum, dif: acum - last.mensual[k] }];
  }));
  return { tramos, filas, diasTotal: tramos.reduce((a, t) => a + t.dias, 0), last };
}
